import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  alpha,
  styled,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useOrgSubscription } from '../../hooks/subscriptions';
import { formatPrice, formatBillingInterval } from '../../utils/subscriptions';
import { AssignPlanDialog } from './AssignPlanDialog';
import { UpgradePlanDialog } from './UpgradePlanDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { SubscriptionStatus } from '../../types/subscriptions';

interface OrganizationSubscriptionTabProps {
  organizationId: string;
  organizationName: string;
}

const getStatusLabel = (status: SubscriptionStatus, trialDaysRemaining?: number) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'Active';
    case SubscriptionStatus.TRIALING:
      if (trialDaysRemaining !== undefined && trialDaysRemaining >= 0) {
        return `Trial - ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left`;
      }
      return 'Trial';
    case SubscriptionStatus.PAST_DUE:
      return 'Past Due';
    case SubscriptionStatus.CANCELED:
      return 'Canceled';
    default:
      return status;
  }
};

const calculateTrialDaysRemaining = (trialEndsAt: string | null): number | undefined => {
  if (!trialEndsAt) return undefined;
  const now = new Date().getTime();
  const trialEnd = new Date(trialEndsAt).getTime();
  const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, daysRemaining);
};

const PlanCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '16px',
  padding: '32px',
  color: '#fff',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.4,
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  height: '100%',
  border: '1px solid #e3e8ef',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    borderColor: '#667eea',
  },
}));

const StatusChip = styled(Chip)<{ status: SubscriptionStatus }>(({ theme, status }) => {
  const colors = {
    [SubscriptionStatus.ACTIVE]: { bg: '#4CAF50', text: '#fff' },
    [SubscriptionStatus.TRIALING]: { bg: '#2196F3', text: '#fff' },
    [SubscriptionStatus.PAST_DUE]: { bg: '#FF9800', text: '#fff' },
    [SubscriptionStatus.CANCELED]: { bg: '#f44336', text: '#fff' },
  };

  const color = colors[status] || { bg: '#697386', text: '#fff' };

  return {
    backgroundColor: color.bg,
    color: color.text,
    fontWeight: 700,
    fontSize: '13px',
    padding: '6px 16px',
    height: 'auto',
    borderRadius: '8px',
    '& .MuiChip-icon': {
      color: color.text,
    },
  };
});

export const OrganizationSubscriptionTab = ({
  organizationId,
  organizationName,
}: OrganizationSubscriptionTabProps) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { data: subscription, isLoading, error } = useOrgSubscription(organizationId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        Failed to load subscription information
      </Alert>
    );
  }

  if (!subscription) {
    return (
      <Box>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: '12px',
            border: '1px solid #2196F3',
            backgroundColor: alpha('#2196F3', 0.05),
          }}
        >
          This organization does not have an active subscription plan.
        </Alert>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAssignDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '10px',
            padding: '12px 32px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '15px',
            boxShadow: '0 4px 14px 0 rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.4)',
            },
          }}
        >
          Assign Plan
        </Button>

        <AssignPlanDialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          organizationId={organizationId}
          organizationName={organizationName}
        />
      </Box>
    );
  }

  // Calculate period progress
  const now = new Date().getTime();
  const start = new Date(subscription.currentPeriodStart).getTime();
  const end = new Date(subscription.currentPeriodEnd).getTime();
  const progress = ((now - start) / (end - start)) * 100;
  const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

  // Calculate trial days remaining
  const trialDaysRemaining = calculateTrialDaysRemaining(subscription.trialEndsAt);
  const isTrial = subscription.status === SubscriptionStatus.TRIALING;
  const isTrialExpiringSoon = isTrial && trialDaysRemaining !== undefined && trialDaysRemaining <= 7;

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3} gap={2}>
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={() => setUpgradeDialogOpen(true)}
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#764ba2',
              backgroundColor: alpha('#667eea', 0.05),
            },
          }}
        >
          Change Plan
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={() => setCancelDialogOpen(true)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancel Subscription
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Plan Card */}
        <Grid item xs={12}>
          <PlanCard>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <StarIcon sx={{ fontSize: 28, color: '#ffd700' }} />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        fontSize: '32px',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {subscription.plan?.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: 0.9,
                      fontSize: '15px',
                      mb: 2,
                    }}
                  >
                    {subscription.plan?.description}
                  </Typography>
                </Box>
                <StatusChip
                  label={getStatusLabel(subscription.status, trialDaysRemaining)}
                  status={subscription.status}
                  icon={
                    subscription.status === SubscriptionStatus.ACTIVE
                      ? <CheckCircleIcon />
                      : subscription.status === SubscriptionStatus.TRIALING
                        ? <AccessTimeIcon />
                        : undefined
                  }
                />
              </Box>

              <Box
                display="flex"
                alignItems="baseline"
                gap={1}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '20px 24px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: '48px',
                    lineHeight: 1,
                  }}
                >
                  {subscription.plan ? formatPrice(subscription.plan.basePrice) : 'N/A'}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    opacity: 0.9,
                    fontSize: '18px',
                    fontWeight: 600,
                  }}
                >
                  {subscription.plan ? formatBillingInterval(subscription.plan.billingInterval) : ''}
                </Typography>
              </Box>
            </Box>
          </PlanCard>
        </Grid>

        {/* Billing Period Card */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarTodayIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#697386',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                  }}
                >
                  Current Billing Period
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1a1f36',
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {daysRemaining} days remaining
                </Typography>
              </Box>
            </Box>

            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" sx={{ color: '#697386', fontSize: '13px' }}>
                  {new Date(subscription.currentPeriodStart).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </Typography>
                <Typography variant="body2" sx={{ color: '#697386', fontSize: '13px' }}>
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(Math.max(progress, 0), 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e3e8ef',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>

            <Box display="flex" gap={2} mt={3}>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#697386',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  Start Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1a1f36',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {new Date(subscription.currentPeriodStart).toLocaleDateString()}
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#697386',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  End Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1a1f36',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </InfoCard>
        </Grid>

        {/* Payment Info Card */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachMoneyIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#697386',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                  }}
                >
                  Pricing Details
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1a1f36',
                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {subscription.plan ? formatPrice(subscription.plan.basePrice) : 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="#697386">
                  Base Price
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#1a1f36">
                  {subscription.plan ? formatPrice(subscription.plan.basePrice) : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="#697386">
                  Billing Interval
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#1a1f36">
                  {subscription.plan ? subscription.plan.billingInterval : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="#697386">
                  Plan
                </Typography>
                <Chip
                  label={subscription.plan?.name || 'N/A'}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '11px',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                  }}
                />
              </Box>
            </Stack>
          </InfoCard>
        </Grid>

        {/* Trial Expiration Warning - shown when < 7 days left */}
        {isTrialExpiringSoon && trialDaysRemaining !== undefined && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{
                borderRadius: '12px',
                border: '1px solid #FF9800',
                backgroundColor: alpha('#FF9800', 0.08),
                '& .MuiAlert-icon': {
                  color: '#FF9800',
                },
              }}
            >
              <Typography variant="body2" fontWeight={700} color="#E65100">
                Trial Expiring Soon!
              </Typography>
              <Typography variant="body2" color="#5D4037">
                {trialDaysRemaining === 0
                  ? 'Your trial expires today! Upgrade now to continue using all features.'
                  : trialDaysRemaining === 1
                    ? 'Your trial expires tomorrow. Upgrade now to avoid service interruption.'
                    : `Only ${trialDaysRemaining} days left in your trial. Upgrade now to continue using all features.`
                }
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Trial Period Info - shown when trial is active but not expiring soon */}
        {isTrial && subscription.trialEndsAt && !isTrialExpiringSoon && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              icon={<TrendingUpIcon />}
              sx={{
                borderRadius: '12px',
                border: '1px solid #2196F3',
                backgroundColor: alpha('#2196F3', 0.05),
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Trial Period Active
              </Typography>
              <Typography variant="body2" color="#697386">
                Your trial ends on {new Date(subscription.trialEndsAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                {trialDaysRemaining !== undefined && ` (${trialDaysRemaining} days remaining)`}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Cancellation Info if applicable */}
        {subscription.canceledAt && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              sx={{
                borderRadius: '12px',
                border: '1px solid #FF9800',
                backgroundColor: alpha('#FF9800', 0.05),
              }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Subscription Canceled
              </Typography>
              <Typography variant="body2" color="#697386">
                Canceled on {new Date(subscription.canceledAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
              {subscription.cancellationReason && (
                <Typography variant="body2" color="#697386" sx={{ mt: 1 }}>
                  <strong>Reason:</strong> {subscription.cancellationReason}
                </Typography>
              )}
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Dialogs */}
      <UpgradePlanDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        organizationId={organizationId}
        organizationName={organizationName}
        currentSubscription={subscription}
      />

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        organizationId={organizationId}
        organizationName={organizationName}
        currentSubscription={subscription}
      />
    </Box>
  );
};
